import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, AlertTriangle, Package, ArrowRightLeft } from "lucide-react";

export default function InventoryPage() {
  const [stockSummary, setStockSummary] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWh, setSelectedWh] = useState("");
  const [showGrnDialog, setShowGrnDialog] = useState(false);
  const [grnForm, setGrnForm] = useState({ sku_id: "", warehouse_id: "", batch_number: "", manufacturing_date: "", expiry_date: "", quantity: 0 });

  const loadData = async () => {
    try {
      const [stockRes, alertsRes, whRes, skuRes] = await Promise.all([
        api.get("/stock/summary", { params: { warehouse_id: selectedWh || undefined } }),
        api.get("/stock/alerts"),
        api.get("/warehouses"),
        api.get("/skus"),
      ]);
      setStockSummary(stockRes.data);
      setAlerts(alertsRes.data);
      setWarehouses(whRes.data);
      setSkus(skuRes.data);
    } catch (err) { console.error("InventoryPage error:", err); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [selectedWh]);

  const handleGrn = async () => {
    try {
      await api.post("/stock/grn", grnForm);
      toast.success("GRN created successfully");
      setShowGrnDialog(false);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

  return (
    <div data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Stock & Inventory</h1>
          <p className="text-sm text-zinc-500 mt-1">Track stock levels across warehouses</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setGrnForm({ sku_id: "", warehouse_id: "", batch_number: "", manufacturing_date: "", expiry_date: "", quantity: 0 }); setShowGrnDialog(true); }} className="rounded-sm h-8 text-xs" data-testid="create-grn-button">
            <Plus size={14} /> Add Stock (GRN)
          </Button>
        </div>
      </div>

      <Tabs defaultValue="stock" className="space-y-4">
        <TabsList className="bg-zinc-100 rounded-sm" data-testid="inventory-tabs">
          <TabsTrigger value="stock" className="rounded-sm text-xs" data-testid="tab-stock">Stock Levels</TabsTrigger>
          <TabsTrigger value="alerts" className="rounded-sm text-xs" data-testid="tab-alerts">
            Alerts {alerts.length > 0 && <span className="ml-1 px-1.5 py-0 text-[9px] bg-red-100 text-red-700 rounded-full">{alerts.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <div className="mb-4">
            <Select value={selectedWh || "all"} onValueChange={(v) => setSelectedWh(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 h-8 text-xs rounded-sm" data-testid="warehouse-filter">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map(wh => <SelectItem key={wh._id} value={wh._id}>{wh.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full" data-testid="stock-table">
              <thead>
                <tr><th>SKU Code</th><th>Product Name</th><th>Warehouse</th><th className="num">Received</th><th className="num">Dispatched</th><th className="num">Available</th><th className="num">Reserved</th><th className="num">Damaged</th><th>Status</th></tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
                stockSummary.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">No stock data. Use GRN to add stock.</td></tr> :
                stockSummary.map((s, i) => {
                  const isLow = s.reorder_level > 0 && s.total_available <= s.reorder_level;
                  const isOut = s.total_available === 0;
                  return (
                    <tr key={i} data-testid={`stock-row-${i}`}>
                      <td className="font-mono text-xs text-zinc-500">{s.sku_code}</td>
                      <td className="font-medium text-zinc-900">{s.sku_name}</td>
                      <td>{s.warehouse_name}</td>
                      <td className="num font-mono">{fmt(s.total_received)}</td>
                      <td className="num font-mono">{fmt(s.total_dispatched)}</td>
                      <td className={`num font-mono font-semibold ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-zinc-900'}`}>{fmt(s.total_available)}</td>
                      <td className="num font-mono">{fmt(s.total_reserved)}</td>
                      <td className="num font-mono">{fmt(s.total_damaged)}</td>
                      <td>
                        {isOut ? <span className="status-danger px-2 py-0.5 text-[10px] font-semibold rounded-sm">Out of Stock</span> :
                         isLow ? <span className="status-warning px-2 py-0.5 text-[10px] font-semibold rounded-sm">Low Stock</span> :
                         <span className="status-active px-2 py-0.5 text-[10px] font-semibold rounded-sm">In Stock</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-2" data-testid="alerts-list">
            {alerts.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-8">No alerts at this time</p>
            ) : alerts.map((alert, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 border rounded-sm ${alert.severity === 'critical' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`alert-${i}`}>
                <AlertTriangle size={16} className={alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'} />
                <div>
                  <p className={`text-xs font-medium ${alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>{alert.message}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{alert.type.replace('_', ' ').toUpperCase()}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* GRN Dialog */}
      <Dialog open={showGrnDialog} onOpenChange={setShowGrnDialog}>
        <DialogContent className="max-w-md" data-testid="grn-dialog">
          <DialogHeader><DialogTitle className="font-heading">Goods Receipt Note (GRN)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">SKU</Label>
              <Select value={grnForm.sku_id} onValueChange={(v) => setGrnForm({...grnForm, sku_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="grn-sku-select"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                <SelectContent>{skus.filter(s => s.status === 'Active').map(s => <SelectItem key={s._id} value={s._id}>{s.name} ({s.sku_id})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Warehouse</Label>
              <Select value={grnForm.warehouse_id} onValueChange={(v) => setGrnForm({...grnForm, warehouse_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="grn-warehouse-select"><SelectValue placeholder="Select Warehouse" /></SelectTrigger>
                <SelectContent>{warehouses.map(wh => <SelectItem key={wh._id} value={wh._id}>{wh.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Batch Number</Label><Input value={grnForm.batch_number} onChange={(e) => setGrnForm({...grnForm, batch_number: e.target.value})} placeholder="e.g. BATCH-001" className="mt-1 rounded-sm" data-testid="grn-batch-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Mfg Date</Label><Input type="date" value={grnForm.manufacturing_date} onChange={(e) => setGrnForm({...grnForm, manufacturing_date: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Expiry Date</Label><Input type="date" value={grnForm.expiry_date} onChange={(e) => setGrnForm({...grnForm, expiry_date: e.target.value})} className="mt-1 rounded-sm" /></div>
            </div>
            <div><Label className="text-xs">Quantity</Label><Input type="number" value={grnForm.quantity} onChange={(e) => setGrnForm({...grnForm, quantity: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="grn-quantity-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrnDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleGrn} className="rounded-sm text-xs" data-testid="save-grn-button">Record GRN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
